import React, { ReactNode, useCallback, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import loadable from 'components/utils/lazyImport'

import MarkdownRender from 'components/Luxkit/MarkdownRender'
import NavigationBar from './NavigableOfferContentBar'
import TraderInformation from './TraderInformation'
import PropertyFinePrint from 'components/Common/BedbankPropertyFinePrint'
import LayoutContainer from 'components/Common/LayoutContainer'
import { PROPERTY_MAP_MARKER_TYPE } from 'constants/map'
import { scrollToId } from 'lib/window/scrollUtils'
import BodyText from 'components/Luxkit/Typography/BodyText'
import NavigableOfferContentGroup from './NavigableOfferContentGroup'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LineCheckIcon from 'components/Luxkit/Icons/line/LineCheckIcon'
import Modal from 'components/Luxkit/Modal/Modal'
import UserReviewList from './UserReviewList'
import TextLink from 'components/Luxkit/TextLink'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const PropertyMap = loadable(() => import(/* webpackChunkName: "PropertyMap" */ 'components/Common/PropertyMap'))

interface Props {
  hash?: string;
  offer: App.BedbankOffer;
  traderInformation?: App.TraderInformation;
}

const ContentWrapper = styled(LayoutContainer)`
  > * {
    margin-top: ${rem(24)};
    padding-top: ${rem(24)};

    & + * {
      border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
    }
  }
`

const TwoColumns = styled.div`
  ${mediaQueryUp.tablet} {
    columns: 2;

    > * {
      break-inside: avoid;
    }
  }
`

const FacilitiesList = styled.ul`
  list-style-type: none;
  margin: 0;
`

const Category = styled.li`
  display: flex;
  align-items: center;
  margin: 0 0 ${rem(16)};

  & > svg {
    min-width: ${rem(20)};
    margin-right: ${rem(12)};
  }
`

const HighlightsWrapper = styled.div`
  & > * + * {
    margin-top: ${rem(16)};
  }
`

function offerHasCoordinates(offer: App.BedbankOffer): boolean {
  return !!(offer.property?.latitude && offer.property.longitude)
}

interface ContentField {
  id: string;
  label: string | ReactNode;
  open: boolean;
  initialHeight?: number;
  minimumContentHeight?: number;
  contentComponent: React.ReactElement;
}

function getContentFields(props: Props): Array<ContentField> {
  const {
    hash,
    offer,
  } = props

  const {
    property: {
      finePrint,
      finePrintOverride,
      location,
      id,
      longitude,
      latitude,
      aboutThisProperty,
      attractionsOverride,
      highlights,
      facilitiesOverride,
    },
    promotions,
  } = offer

  const hasPromotions = offer.promotions.length > 0

  const promoFinePrint: string | null = promotions.filter(p => p.finePrint).map(p => p.finePrint)?.at(0) || null

  const contentFields: Array<ContentField> = []

  if (offerHasCoordinates(offer)) {
    contentFields.push({
      id: 'getting-there',
      label: 'Location',
      open: hash === '#getting-there',
      initialHeight: 410,
      contentComponent: <VerticalSpacer gap={16}>
        <BodyText variant="large" weight="bold" data-testid="offer-location">{location}</BodyText>
        <PropertyMap
          height={316}
          wrapperStyle={{ height: 316 }}
          markers={[{
            id,
            type: PROPERTY_MAP_MARKER_TYPE,
            latitude,
            longitude,
          }]}
        />
        {offer.attractions &&
          <div>
            <BodyText variant="large" weight="bold" data-testid="offer-location">
              What's nearby
            </BodyText>
            <MarkdownRender content={attractionsOverride ?? offer.attractions} />
          </div>
        }
      </VerticalSpacer>,
    })
  }

  if (hasPromotions && (highlights || aboutThisProperty?.length)) {
    contentFields.push({
      id: 'highlights',
      label: 'Highlights',
      open: hash === '#highlights',
      initialHeight: 120,
      contentComponent: <HighlightsWrapper>
        {highlights && <MarkdownRender content={highlights} />}
        {!highlights && aboutThisProperty?.map(({ name, values }) => (<div key={name}>
          <BodyText variant="large" weight="bold">{name}</BodyText>
          {values.map((value, index) => (
            <MarkdownRender key={index} content={value} />
          ))}
        </div>))}
      </HighlightsWrapper>,
    })
  }

  if (offer.facilityGroups || facilitiesOverride) {
    contentFields.push({
      id: 'facilities-all',
      label: 'Facilities',
      open: hash === '#facilities-all',
      initialHeight: 120,
      contentComponent: <TwoColumns>
        {facilitiesOverride && (
          <div data-testid="offer-location">
            <MarkdownRender content={facilitiesOverride} />
          </div>
        )}
        {!facilitiesOverride && offer.facilityGroups.map(facilityGroup => (
          <div key={facilityGroup.name}>
            <BodyText variant="large" weight="bold" data-testid="offer-location">{facilityGroup.name}</BodyText>
            <FacilitiesList>
              {facilityGroup.values.map(value => (
                <li key={value.name}>{value.name}</li>
              ))}
            </FacilitiesList>
          </div>
        ))}
      </TwoColumns>,
    })
  }

  if (finePrint?.safety?.length) {
    contentFields.push({
      id: 'fine-print-safety',
      label: 'Cleaning and safety measures',
      contentComponent: <TwoColumns>
        {finePrint.safety.map(safetyAttribute => (
          <Category key={safetyAttribute}>
            <LineCheckIcon size="S" />{safetyAttribute}
          </Category>
        ))}
      </TwoColumns>,
      open: hash === '#fine-print-safety',
    })
  }

  if (finePrintOverride) {
    contentFields.push({
      id: 'fine-print',
      label: 'Fine print',
      contentComponent: <MarkdownRender content={finePrintOverride} />,
      open: hash === '#fine-print',
    })
  } else if (finePrint || promoFinePrint) {
    contentFields.push({
      id: 'fine-print',
      label: 'Fine print',
      contentComponent: <>
        {promoFinePrint &&
          <MarkdownRender content={promoFinePrint} />
        }
        {!promoFinePrint && finePrint && Object.keys(finePrint).length > 0 &&
          <PropertyFinePrint
              finePrint={finePrint}
            />
        }</>,
      open: hash === '#fine-print',
    })
  }

  if (offer.property?.rating?.reviewsTotal && offer.property?.rating.score) {
    contentFields.push({
      id: 'customer-reviews',
      label: 'Reviews',
      open: hash === '#customer-reviews',
      initialHeight: 200,
      minimumContentHeight: 2000,
      contentComponent: <UserReviewList property={offer.property} isLPP />,
    })
  }

  return contentFields
}

function BedbankNavigableOfferContent(props: Props) {
  const contentFields = getContentFields(props)
  const defaultOpenField = contentFields.find(field => field.open)
  const [openFields, setOpenFields] = useState<Record<string, boolean>>({ [defaultOpenField?.id]: true })
  const [headerSelected, setHeaderSelected] = useState<string>(defaultOpenField?.id)
  const [modalOpen, setModalOpen] = useState<boolean>(false)

  const onGroupInView = (id: string, inView: boolean) => {
    if (inView) {
      setHeaderSelected(id)
    }
  }

  const setFieldToggle = useCallback((id: string, toggled: boolean) => {
    window.setTimeout(() => {
      scrollToId(id, -54)
    }, 300)

    setOpenFields({
      ...openFields,
      [id]: toggled,
    })
  }, [openFields])

  const onHeaderClick = useCallback((id: string) => {
    setFieldToggle(id, true)
  }, [setFieldToggle])

  const onReadMoreToggle = useCallback((id: string) => {
    setFieldToggle(id, !openFields[id])
  }, [setFieldToggle, openFields])

  const onModalOpenToggle = useCallback(() => {
    setModalOpen(!modalOpen)
  }, [setModalOpen, modalOpen])

  return (
    <>
      <NavigationBar
        contentFields={contentFields}
        onClick={onHeaderClick}
        selectedHeader={headerSelected}
      />
      <ContentWrapper size="md">
        {contentFields.map(contentField => <NavigableOfferContentGroup
            key={contentField.id}
            heading={contentField.label}
            id={contentField.id}
            onInViewChange={onGroupInView}
            initialHeight={contentField.initialHeight ?? 96}
            minimumContentHeight={contentField.minimumContentHeight ?? 0}
            open={!!openFields[contentField.id]}
            onToggle={onReadMoreToggle}
          >
          <BodyText variant="large">
            {contentField.contentComponent}
          </BodyText>
        </NavigableOfferContentGroup>)}
        {props.traderInformation && (
          <div id="trader-information">
            <TextLink size="medium" onClick={onModalOpenToggle}>
              About this supplier
            </TextLink>
            <Modal
              title="About this supplier"
              isOpen={modalOpen}
              onClose={onModalOpenToggle}
            >
              <TraderInformation traderInformation={props.traderInformation} />
            </Modal>
          </div>
        )}
      </ContentWrapper>
    </>
  )
}

export default BedbankNavigableOfferContent
